import { handleVersionMismatch } from 'actions/versionSync';
import * as MapActionTypes from 'actiontypes/map';
import { VersionMismatch } from 'services/BaseService';
import MapService from 'services/MapService';
import preloadMapSprites from 'components/Map/preloadMapSprites';
import { isDrawable, isLodgings } from 'components/Map/util';
import * as SpriteCaches from 'components/Map/ReactLeafletPixiOverlay/sprite-caches';
import asStateAwareArea from 'mapping/asStateAwareArea';
import { IArea, IMapService, IStateAwareArea } from 'types/map';
import { IAppState } from 'types/app';
import isWebGLSupported from 'startup/isWebGLSupported';
import { IBootstrapOptions } from 'actions/app/bootstrap';

/** ----------------------------------------------------------------------------
 * FETCH MAP
 -----------------------------------------------------------------------------*/

export default fetch(new MapService());

export function fetch(service: IMapService) {
  return (options?: IBootstrapOptions) => async (dispatch: Function, getState: () => IAppState) => {
    dispatch(mapFetchRequested());

    try {
      const { data } = await service.fetch();
      if (!data.isSuccess) {
        return dispatch(mapFetchFailure(data));
      }

      // If we have map data, load sprites
      if (data.areas) {
        dispatch(mapFetchSuccess(data));
        const { map: { fallbackMapPreferred } } = getState();

        if (fallbackMapPreferred || !isWebGLSupported()) {
          console.info('Fallback map preferred; not loading map sprites');
        } else {
          if (options?.fetchSpritesNow ?? true) {
            // This should be OK, because it won't run twice
            await preloadMapSprites(
              data.areas.filter((area: IArea) => isDrawable(area) && !isLodgings(area)),
              options?.onSpriteLoadProgress,
            );
            // Create state-aware areas, then update their sprites
            const stateAwareAreas: IStateAwareArea[] = data.areas
              .map((area: IArea) => asStateAwareArea(area, data.areas, data.currentArea));
            await Promise.all(stateAwareAreas.map(SpriteCaches.updateSpriteForArea));
          }
        }
      }
      return data;

    } catch (err) {
      if (err instanceof VersionMismatch) {
        dispatch(handleVersionMismatch(err));
        return;
      }
      console.error(err);
      dispatch(mapFetchFailure(err));
    }
  };
}

const mapFetchRequested = () => ({
  type: MapActionTypes.FETCH_MAP_REQUESTED,
  isFetching: true,
});

const mapFetchSuccess = (data: any) => ({
  type: MapActionTypes.FETCH_MAP_SUCCESS,
  isFetching: false,
  currentArea: data.currentArea,
  areas: data.areas,
  payload: {
    currentArea: data.currentArea,
    areas: data.areas,
  },
});

const mapFetchFailure = (error: any) => ({
  type: MapActionTypes.FETCH_MAP_FAILURE,
  isFetching: false,
  error: true,
  status: error.response && error.response.status,
});